import type { OpsIsoWeekday, OpsParity, OpsWeekInMonth } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

export function assertMonth(month: number): void {
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError(`Invalid month: ${month}`);
  }
}

export function toUtcDate(year: number, month: number, day: number): Date {
  assertMonth(month);
  return new Date(Date.UTC(year, month - 1, day));
}

export function parseIsoDate(value: string): Date {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`Invalid ISO date: ${value}`);
  }
  return parsed;
}

export function formatIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function getDaysInMonth(year: number, month: number): number {
  assertMonth(month);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function getMonthDates(year: number, month: number): Date[] {
  const count = getDaysInMonth(year, month);
  return Array.from({ length: count }, (_, index) => toUtcDate(year, month, index + 1));
}

export function getIsoWeekday(date: Date): OpsIsoWeekday {
  const day = date.getUTCDay();
  return (day === 0 ? 7 : day) as OpsIsoWeekday;
}

export function getIsoWeekNumber(date: Date): number {
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil((((utc.getTime() - yearStart.getTime()) / DAY_MS) + 1) / 7);
}

export function getIsoWeekParity(date: Date): OpsParity {
  return getIsoWeekNumber(date) % 2 === 0 ? 'EVEN' : 'ODD';
}

export function isSameOrAfterIsoDate(value: string, cutoff: string): boolean {
  return parseIsoDate(value).getTime() >= parseIsoDate(cutoff).getTime();
}

export function getWeekInMonth(date: Date): 1 | 2 | 3 | 4 | 'LAST' {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const weekday = getIsoWeekday(date);
  const matchingDays = getMonthDates(year, month).filter((candidate) => getIsoWeekday(candidate) === weekday);
  const index = matchingDays.findIndex((candidate) => candidate.getTime() === date.getTime());
  if (index === -1) {
    throw new Error('Date is not in its own month');
  }
  if (index === matchingDays.length - 1) {
    return 'LAST';
  }
  return (index + 1) as 1 | 2 | 3 | 4;
}

export function getNthWeekdayOfMonth(
  year: number,
  month: number,
  weekday: OpsIsoWeekday,
  occurrence: OpsWeekInMonth,
): Date | null {
  const matchingDays = getMonthDates(year, month).filter((candidate) => getIsoWeekday(candidate) === weekday);
  if (occurrence === 'LAST') {
    return matchingDays.at(-1) ?? null;
  }
  return matchingDays[occurrence - 1] ?? null;
}

export function isMonthListed(months: readonly number[], month: number): boolean {
  return months.includes(month);
}

export function uniqueDates(dates: readonly Date[]): Date[] {
  const seen = new Set<string>();
  const result: Date[] = [];
  for (const date of dates) {
    const key = formatIsoDate(date);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(date);
    }
  }
  return result;
}
